import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Avatar, Box, Chip, IconButton, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useEffect, useState } from 'react'
import {
  ImageField,
  ImageInput,
  RecordContextProvider,
  ReferenceField,
  required,
  TextField,
  TextInput,
  useGetList,
  useReference,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'

import IncidentCategorySelectInput from './IncidentCategorySelectInput'
import MarkerInput from './input'
import OwnershipSelectInput from './OwnershipSelectInput'
import SeveritySelectInput from './SeveritySelectInput'
import AdvancedReferenceInput from '../../components/AdvancedReferenceInput'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import { VEHICLE_TYPES_ICONS } from '../../config/vehicles'
import { AdvancedSimpleForm } from '../common/forms'
import { ActionModalSection } from './actions/common'
import {
  INCIDENT_REPORT_CATEGORY_NFC_READER,
  INCIDENT_REPORT_STATUS_REPORTED,
  INCIDENT_REPORT_STATUS_VALIDATED,
} from '../../config/damages'
import damageBlueprintsConfig from '../damageBlueprints/config'
import vehiclesConfig from '../vehicles/config'

const IconValueRow = ({ icon: Icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    {Icon ? <Icon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} /> : null}
    {children}
  </Box>
)

export const BlueprintReferenceInput = ({ readOnly }) => {
  const translate = useTranslate()
  const vehicleId = useWatch({ name: 'vehicle_id' })
  const { referenceRecord: vehicleData } = useReference({
    reference: vehiclesConfig.name,
    id: vehicleId,
    options: { enabled: Boolean(vehicleId) },
  })
  const blueprintSet = vehicleData?.blueprint_set
  return (
    <AdvancedReferenceInput
      emptyText={translate(`resources.${damageBlueprintsConfig.name}.invisible`)}
      optionText={(choice) => `resources.${damageBlueprintsConfig.name}.enums.name.${choice?.name}`}
      reference={damageBlueprintsConfig.name}
      source={damageBlueprintsConfig.options.referenceKey}
      readOnly={readOnly || !Boolean(vehicleId)}
      translateChoice={true}
      filter={{ set: blueprintSet }}
    />
  )
}

const VehicleAssignmentSection = () => {
  const translate = useTranslate()
  const vehicleId = useWatch({ name: 'vehicle_id' })
  const { referenceRecord: vehicle } = useReference({
    reference: vehiclesConfig.name,
    id: vehicleId,
    options: { enabled: Boolean(vehicleId) },
  })

  const { total: reportedIncidentCount } = useGetList(
    'incident-reports',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_on', order: 'DESC' },
      filter: {
        vehicle_id: vehicleId,
        status: INCIDENT_REPORT_STATUS_REPORTED,
      },
    },
    { enabled: Boolean(vehicleId) },
  )
  const { total: validatedIncidentCount } = useGetList(
    'incident-reports',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'created_on', order: 'DESC' },
      filter: {
        vehicle_id: vehicleId,
        status: INCIDENT_REPORT_STATUS_VALIDATED,
      },
    },
    { enabled: Boolean(vehicleId) },
  )
  const activeIncidentCount = (reportedIncidentCount || 0) + (validatedIncidentCount || 0)
  const VehicleTypeIcon = vehicle?.type ? VEHICLE_TYPES_ICONS[vehicle.type]?.type : null
  const vehicleIdentity = [vehicle?.brand, vehicle?.model].filter(Boolean).join(' ')
  const vehiclePlate = vehicle?.designation
  const hasVehicle = Boolean(vehicleId && vehicle)
  const incidentLabel =
    activeIncidentCount > 0
      ? translate('resources.incident-reports.forms.helpers.activeIncidentCount', {
          smart_count: activeIncidentCount,
        })
      : ''

  return (
    <ActionModalSection
      titleKey="resources.incident-reports.forms.sections.vehicleAssignment"
      titleIcon={DirectionsCarOutlinedIcon}
      sx={{ height: '142px' }}
      rightElement={
        <Box
          sx={{
            p: 2,
            minHeight: 140,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              height: 88,
              borderRadius: 1.5,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'action.hover',
            }}
          >
            {hasVehicle && vehicle?.picture ? (
              <ImageField
                source="picture"
                record={vehicle}
                title="model"
                sx={{
                  width: '100%',
                  height: '100%',
                  '& img': {
                    display: 'block',
                    width: '100% !important',
                    height: '100% !important',
                    objectFit: 'contain',
                    objectPosition: 'center',
                  },
                }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{
                  width: 68,
                  height: 68,
                  mx: 'auto',
                  backgroundColor: 'action.hover',
                  color: 'text.secondary',
                }}
              >
                <DirectionsCarOutlinedIcon sx={{ fontSize: 32 }} />
              </Avatar>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasVehicle ? (
              activeIncidentCount > 0 ? (
                <Chip
                  icon={<WarningAmberOutlinedIcon />}
                  label={incidentLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                    backgroundColor: 'action.hover',
                    color: 'text.secondary',
                    fontWeight: 600,
                    width: 'fit-content',
                    '& .MuiChip-icon': { color: 'inherit', fontSize: 18 },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.08)',
                    color: 'success.main',
                    width: 'fit-content',
                  }}
                >
                  <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 17, color: 'inherit' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'inherit' }}>
                    {translate('resources.incident-reports.forms.helpers.noActiveIncident')}
                  </Typography>
                </Box>
              )
            ) : null}
          </Box>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.9,
          width: '100%',
          '& .MuiFormControl-root': { m: 0, width: '100%' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <AdvancedReferenceInput
            sx={{ mt: 0.75 }}
            source="vehicle_id"
            reference="vehicles"
            label={false}
            placeholderContent={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.disabled' }}>
                <SearchOutlinedIcon sx={{ fontSize: 18, color: 'inherit' }} />
                <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.2 }}>
                  {translate('resources.vehicles.forms.helpers.searchPlaceholder')}
                </Typography>
              </Box>
            }
            TextFieldProps={{
              sx: {
                '& .MuiInputBase-root': {
                  minHeight: 40,
                },
              },
            }}
          />

          <Box sx={{ mt: 0.85, minHeight: 30 }}>
            {hasVehicle ? (
              <RecordContextProvider value={vehicle}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    columnGap: 2,
                    rowGap: 0.5,
                    alignItems: 'start',
                    width: '100%',
                  }}
                >
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                    <IconValueRow icon={VehicleTypeIcon || DirectionsCarOutlinedIcon}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.2 }}
                        noWrap
                      >
                        {vehicleIdentity || translate('resources.incident-reports.show.summary.none')}
                      </Typography>
                    </IconValueRow>
                    <IconValueRow icon={ConfirmationNumberOutlinedIcon}>
                      <TextField source="designation" />
                    </IconValueRow>
                  </Box>
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    <IconValueRow icon={BusinessOutlinedIcon}>
                      <ReferenceField reference="organisations" source="organisation_id" link={false} />
                    </IconValueRow>
                    <IconValueRow icon={LocationOnOutlinedIcon}>
                      <ReferenceField reference="hubs" source="hub_id" link={false} />
                    </IconValueRow>
                  </Box>
                </Box>
              </RecordContextProvider>
            ) : null}
          </Box>
        </Box>
      </Box>
    </ActionModalSection>
  )
}

const IncidentReportImageDropzone = () => {
  const translate = useTranslate()
  const { setValue } = useFormContext()
  const pictureData = useWatch({ name: 'picture_data' })
  const selectedPicture = Array.isArray(pictureData) ? pictureData[0] : pictureData
  const previewSource = selectedPicture?.rawFile?.preview ?? selectedPicture?.src ?? selectedPicture?.preview

  const handleRemovePicture = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setValue('picture_data', null, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        py: 1.25,
        px: 1.5,
        minHeight: 120,
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {previewSource ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.18)), url("${previewSource}")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.35,
          width: '100%',
          minHeight: 120,
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 26, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
          {translate('resources.incident-reports.forms.helpers.imageDropzoneTitle')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
          {translate('resources.incident-reports.forms.helpers.imageDropzoneHint')}
        </Typography>
      </Box>
      {previewSource ? (
        <IconButton
          aria-label={translate('ra.action.delete')}
          onClick={handleRemovePicture}
          onMouseDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1,
            width: 22,
            height: 22,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      ) : null}
    </Box>
  )
}

const EvidenceSection = () => {
  const translate = useTranslate()

  return (
    <ActionModalSection
      titleKey="resources.incident-reports.forms.sections.evidence"
      titleIcon={ImageOutlinedIcon}
      helperKey="resources.incident-reports.forms.helpers.evidence"
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(240px, 0.9fr)' },
          gap: 1.5,
          alignItems: 'start',
          width: '100%',
          '& .MuiFormControl-root': { m: 0, width: '100%' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.75 }}>
            {translate('resources.incident-reports.forms.helpers.evidenceDescription')}
          </Typography>
          <TextInput multiline minRows={6} maxRows={6} fullWidth source="note" validate={required()} />
        </Box>
        <ImageInput
          accept={{ 'image/*': ['.jpeg', '.png', '.webp'] }}
          placeholder={<IncidentReportImageDropzone />}
          sx={{
            width: '100%',
            marginLeft: 0,
            '& .RaFileInput-dropZone': {
              minHeight: 120,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'action.hover',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.selected',
              },
            },
            '& .previews': {
              display: 'none',
            },
          }}
          helperText={false}
          source="picture_data"
        />
      </Box>
    </ActionModalSection>
  )
}

const ClassificationLocationHint = () => {
  const translate = useTranslate()

  return (
    <Box
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.12)' : '#eef8ff'),
        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.35)' : '#cbe8fb'}`,
        borderRadius: 2,
        px: 1.25,
        py: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        alignSelf: { xs: 'stretch', md: 'center' },
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: (theme) => theme.palette.info.main, mt: 0.1 }} />
      <Typography variant="caption" sx={{ lineHeight: 1.4, color: 'text.secondary', minWidth: 0 }}>
        {translate('resources.incident-reports.forms.helpers.classificationLocation')}
      </Typography>
    </Box>
  )
}

const ClassificationSection = ({ setIsMarkerInputPristine }) => {
  const vehicleId = useWatch({ name: 'vehicle_id' })
  const { referenceRecord: vehicleData } = useReference({
    reference: vehiclesConfig.name,
    id: vehicleId,
    options: { enabled: Boolean(vehicleId) },
  })
  const supportsNfcAccess = Boolean(vehicleData?.nfc_access_supported)

  return (
    <ActionModalSection
      titleKey="resources.incident-reports.forms.sections.classification"
      titleIcon={FactCheckOutlinedIcon}
      helperKey="resources.incident-reports.forms.helpers.classification"
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          columnGap: 2,
          rowGap: 1.25,
          alignItems: 'start',
          width: '100%',
          '& .MuiFormControl-root': { m: 0, width: '100%' },
        }}
      >
        <IncidentCategorySelectInput
          nfcAccessSupported={supportsNfcAccess}
          includeBackendManaged={false}
          validate={required()}
        />
        <SeveritySelectInput source="severity" validate={required()} />
        <OwnershipSelectInput />
      </Box>

      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)' },
          gap: 1.5,
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ minWidth: 0, '& .MuiFormControl-root': { m: 0, width: '100%', maxWidth: 190 } }}>
          <BlueprintReferenceInput />
        </Box>
        <Box
          sx={{
            minWidth: 0,
            height: '95px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            '& > .MuiBox-root': {
              ml: '0 !important',
              mt: '0 !important',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& > .MuiBox-root > div': {
              transform: 'scale(0.74)',
              transformOrigin: 'center',
            },
          }}
        >
          <MarkerInput onNewMarker={() => setIsMarkerInputPristine(false)} />
        </Box>
        <ClassificationLocationHint />
      </Box>
    </ActionModalSection>
  )
}

const IncidentReportFormFields = ({ setIsMarkerInputPristine }) => {
  const { setValue } = useFormContext()
  const [vehicleId, selectedCategory] = useWatch({ name: ['vehicle_id', 'category'] })
  const { referenceRecord: vehicleData, isLoading: isVehicleLoading } = useReference({
    reference: vehiclesConfig.name,
    id: vehicleId,
    options: { enabled: Boolean(vehicleId) },
  })
  const supportsNfcAccess = Boolean(vehicleData?.nfc_access_supported)

  useEffect(() => {
    if (isVehicleLoading || selectedCategory !== INCIDENT_REPORT_CATEGORY_NFC_READER) return
    if (!vehicleId || !supportsNfcAccess) {
      setValue('category', '')
    }
  }, [isVehicleLoading, vehicleId, selectedCategory, setValue, supportsNfcAccess])

  return (
    <>
      <>
        <VehicleAssignmentSection />
        <EvidenceSection />
        <ClassificationSection setIsMarkerInputPristine={setIsMarkerInputPristine} />
      </>
    </>
  )
}

const IncidentReportFormLayout = () => {
  const [isMarkerInputPristine, setIsMarkerInputPristine] = useState(true)

  return (
    <AdvancedSimpleForm
      sx={{ width: '100%', mx: 'auto', px: { xs: 1.5, md: 2 }, boxSizing: 'border-box' }}
      toolbar={<BasicFormToolbar alwaysEnableSaveButton={!isMarkerInputPristine} />}
    >
      <IncidentReportFormFields setIsMarkerInputPristine={setIsMarkerInputPristine} />
    </AdvancedSimpleForm>
  )
}

export default IncidentReportFormLayout
